/**
 * Talking to the public `/api/records` route.
 *
 * The owner's own words: "Include the player name who has the top score for
 * each different gamemode." No session, no credentials the route reads — but
 * `account-io.ts`'s `request()` is reused anyway rather than a sixth copy of
 * "never throw for a reason a player caused, map a failed fetch to
 * 'offline'." Unlike every other `*-io.ts` module in this directory, the
 * server side of this route answers `200` even with no database at all
 * (`apps/server/src/http/records-api.ts`'s own header comment) — so
 * `unavailable` and `signed-out` never actually happen here. They stay in the
 * return type only because `request()`'s `AccountOutcome` always carries
 * them, and a caller that never receives an outcome it does not handle costs
 * nothing for it still being possible in principle.
 */
import { request } from './account-io.js';
import type { AccountOutcome } from './account-io.js';

export const RECORDS_PATH = '/api/records';

/** One mode key's global record — the best result anybody has set, and who set it. See `docs/decisions/0039` for the tie rule. */
export interface SoloRecordEntry {
  readonly modeKey: string;
  readonly result: number;
  readonly formatted: string;
  readonly username: string;
  readonly achievedAtMs: number;
}

/** One variant's leader — the player with the most wins. See `docs/decisions/0039` for the tie rule. */
export interface VersusLeaderEntry {
  readonly variant: string;
  readonly username: string;
  readonly wins: number;
}

/** Everything `GET /api/records` answers. */
export interface PublicRecords {
  readonly solo: readonly SoloRecordEntry[];
  readonly versus: readonly VersusLeaderEntry[];
}

/** Injected in tests; defaults to the page's own `fetch`. */
export type Fetcher = typeof globalThis.fetch;

/** Works signed out, works with no database at all — see this file's header. */
export function fetchRecords(fetcher?: Fetcher): Promise<AccountOutcome<PublicRecords>> {
  return request(RECORDS_PATH, {
    method: 'GET',
    ...(fetcher === undefined ? {} : { fetcher }),
  });
}
